import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy, usePagination } from 'react-table';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { parse, format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheck, faEye, faPlane, faClose, faFileExport } from '@fortawesome/free-solid-svg-icons';

import VendaItem from './VendaItem';
import { NotificationMessage } from './NotificationMessage';

registerLocale('pt-BR', ptBR);

function VendaList() {
    const [vendas, setVendas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVendas, setSelectedVendas] = useState([]);
    const [estados, setEstados] = useState([]);
    const [showExportDropdown, setShowExportDropdown] = useState(false);

    const [notification, setNotification] = useState({
        type: 'info',
        message: '',
        show: false,
    });

    const [filters, setFilters] = useState({
        numero_pedido: '',
        descricao: '',
        valor_fabrica: '',
        data_contrato: '',
        nome: '',
        razao: '',
        status: '',
        criado: '',
    });

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [openDateRange, setOpenDateRange] = useState(false);
    const [resultsPerPage, setResultsPerPage] = useState(20);

    const navigate = useNavigate();
    const location = useLocation();

    const accessToken = localStorage.getItem('access_token');
    const baseUrl = process.env.REACT_APP_API_URL;

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        setFilters(prev => ({
            ...prev,
            dateFrom: start ? format(start, 'dd/MM/yyyy') : '',
            dateTo: end ? format(end, 'dd/MM/yyyy') : '',
        }));
    };

    const updateUrlParams = (newFilters) => {
        const filteredParams = Object.fromEntries(
            Object.entries(newFilters).filter(([_, value]) => value !== '' && value !== null)
        );

        const params = new URLSearchParams(filteredParams).toString();
        navigate(`?${params}`);
    };

    const fetchVendas = async () => {
        try {
            const accessToken = localStorage.getItem('access_token');

            const response = await fetch(`${baseUrl}/manager`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('Erro ao buscar os vendas.');
            }

            const data = await response.json();

            setVendas(data.vendas);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        for (const param of searchParams) {
            setFilters(prev => ({
                ...prev,
                [param[0]]: param[1]
            }));
        }
        fetchVendas();
    }, []);

    const handleCloseNotification = () => {
        setNotification((prev) => ({ ...prev, show: false }));
    };

    const handleSelectAll = (isChecked) => {
        if (isChecked) {
            setSelectedVendas(vendas.map(member => member.id));
        } else {
            setSelectedVendas([]);
        }
    };

    const isAllSelected = selectedVendas.length === vendas.length && vendas.length > 0;

    const handleSelectVenda = (vendaId) => {
        setSelectedVendas(prevSelected =>
            prevSelected.includes(vendaId)
                ? prevSelected.filter(id => id !== vendaId)
                : [...prevSelected, vendaId]
        );
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const updatedFilters = {
            ...filters,
            [name]: value,
        };
        setFilters(updatedFilters);
        updateUrlParams(updatedFilters);
    };

    const handleDeleteSelected = async () => {
        const idsString = selectedVendas.join(',');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/vendas/excluir/${idsString}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao enviar o pedido ao sistema!',
                    show: true,
                });

                throw new Error('Erro ao excluir os itens.');
            }

            setNotification({
                type: 'success',
                message: 'vendas excluídos com sucesso',
                show: true
            });

            setSelectedVendas([]);
            fetchVendas();
        } catch (error) {
            console.error('Erro ao excluir itens:', error);
        }
    };

    const handleApproveSelected = async () => {
        const idsString = selectedVendas.join(',');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/vendas/aprovar/${idsString}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            const result = await response.json();

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: result.message,
                    show: true,
                });

                throw new Error('Erro ao aprovar os itens.');
            }

            setNotification({
                type: 'success',
                message: result.message,
                show: true
            });

            setSelectedVendas([]);
            fetchVendas();
        } catch (error) {
            console.error('Erro ao aprovar itens:', error);
        }
    };

    const handleDisapproveSelected = async () => {
        const idsString = selectedVendas.join(',');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/vendas/reprovar/${idsString}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao enviar o pedido ao sistema!',
                    show: true,
                });

                throw new Error('Erro ao aprovar os itens.');
            }

            setNotification({
                type: 'success',
                message: 'vendas reprovadas com sucesso',
                show: true
            });

            setSelectedVendas([]);
            fetchVendas();
        } catch (error) {
            console.error('Erro ao aprovar itens:', error);
        }
    };

    const handleExport = async (status) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/manager/relatorios/vendas/${status}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                setNotification({
                    type: 'error',
                    message: 'Erro ao exportar relatório!',
                    show: true,
                });
                throw new Error('Erro ao exportar relatório.');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `vendas_${status}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            setNotification({
                type: 'success',
                message: `Relatório de vendas ${status} exportado com sucesso!`,
                show: true
            });

            setShowExportDropdown(false);
        } catch (error) {
            console.error('Erro ao exportar:', error);
        }
    };

    const filteredVendas = useMemo(() => {
        return vendas.filter(venda => {
            const matchesNumeroPedido = venda.numero_pedido.toString().includes(filters.numero_pedido);
            const matchesDescricao = venda.descricao.toLowerCase().includes(filters.descricao.toLowerCase());
            const matchesValorFabrica = venda.valor_fabrica
                ? venda.valor_fabrica.toString().includes(filters.valor_fabrica)
                : '';
            const matchesContrato = venda.data_contrato.toString().includes(filters.data_contrato);
            const matchesNome = venda.nome.toLowerCase().includes(filters.nome.toLowerCase());
            const matchesRazao = venda.razao.toLowerCase().includes(filters.razao.toLowerCase());
            const matchesStatus = venda.status.toLowerCase().includes(filters.status);
            const matchesCriado = venda.criado.toString().includes(filters.criado);

            return matchesNumeroPedido && matchesDescricao && matchesValorFabrica && matchesContrato && matchesNome && matchesRazao && matchesStatus && matchesCriado;
        });
    }, [vendas, filters]);

    const columns = useMemo(() => [
        {
            id: 'select',
            Header: ({ getToggleAllRowsSelectedProps }) => (
                <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                />
            ),
            Cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={selectedVendas.includes(row.original.id)}
                    onChange={() => handleSelectVenda(row.original.id)}
                />
            ),
            headerClassName: 'p-4 text-left text-sm font-medium uppercase'
        },
        { Header: 'Nº do pedido', accessor: 'numero_pedido', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Descrição', accessor: 'descricao', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Valor', accessor: 'valor_fabrica', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Nome', accessor: 'nome', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Loja', accessor: 'razao', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Contrato', accessor: 'data_contrato', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Status', accessor: 'status', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Criado', accessor: 'criado', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
        { Header: 'Ações', accessor: '', headerClassName: 'p-1 text-left 2xl:p-2 text-sm font-medium uppercase select-none' },
    ], [isAllSelected, selectedVendas]);

    const pageParams = new URLSearchParams(window.location.search);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        canPreviousPage,
        canNextPage,
        pageOptions,
        state: { pageIndex },
        gotoPage,
        nextPage,
        previousPage,
    } = useTable(
        {
            columns,
            data: filteredVendas,
            initialState: { pageIndex: pageParams.has('page') ? (pageParams.get('page') - 1) : 0, pageSize: resultsPerPage },
        },
        useSortBy,
        usePagination
    );

    useEffect(() => {
        if (location.state?.message) {
            setNotification({
                type: location.state.message.type,
                message: location.state.message.text,
                show: true,
            });
        }
    }, [location.state]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);

        if (pageIndex !== 0 || searchParams.has('page')) {
            searchParams.set('page', pageIndex + 1);
            navigate(`?${searchParams.toString()}`, { replace: true });
        }
    }, [pageIndex, navigate]);

    if (loading) return (
        <div className="mx-auto py-10">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
        </div>
    );

    if (error) {
        return <div className="text-2xl text-center text-red-600 font-bold my-10">{error}</div>;
    }

    return (
        <>
            <div className="grid grid-cols-6 gap-4 mb-4">
                <div>
                    <label htmlFor="numero_pedido" className="block text-sm font-medium text-gray-700 mb-1">Nº do pedido</label>
                    <input
                        type="text"
                        id="numero_pedido"
                        name="numero_pedido"
                        placeholder="Nº do pedido"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.numero_pedido}
                        onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                    <input
                        type="text"
                        id="descricao"
                        name="descricao"
                        placeholder="Descrição"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.descricao}
                        onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="valor_fabrica" className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                    <input
                        type="text"
                        id="valor_fabrica"
                        name="valor_fabrica"
                        placeholder="Valor"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.valor_fabrica}
                        onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                    <input
                        type="text"
                        id="nome"
                        name="nome"
                        placeholder="Nome"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.nome}
                        onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="razao" className="block text-sm font-medium text-gray-700 mb-1">Razão Social</label>
                    <input
                        type="text"
                        id="razao"
                        name="razao"
                        placeholder="Razão social"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.razao}
                        onChange={handleFilterChange}
                    />
                </div>

                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        id="status"
                        name="status"
                        className="border border-gray-300 text-sm p-2 rounded-md w-full"
                        value={filters.status ? filters.status : 'Aprovado'}
                        onChange={handleFilterChange}
                    >
                        <option value="">---</option>
                        <option value="aprovado">Aprovados</option>
                        <option value="reprovado">Reprovados</option>
                        <option value="pendente">Pendentes</option>
                    </select>
                </div>
            </div>

            <table {...getTableProps()} className="w-full table-auto border-collapse bg-white shadow-md rounded-lg">
                <thead>
                    {headerGroups.map((headerGroup, index) => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();

                        return (
                            <tr key={key || index} {...headerGroupProps} className="bg-gray-100 text-left">
                                {headerGroup.headers.map((column, groupIndex) => {
                                    const { key: colKey, ...columnProps } = column.getHeaderProps(column.getSortByToggleProps({ title: undefined }));

                                    return (
                                        <th
                                            key={colKey || groupIndex}
                                            {...columnProps}
                                            className="px-4 py-2 font-semibold text-gray-700 border-b"
                                        >
                                            {column.render('Header')}
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <svg className="fill-gray-500 inline-block mb-1 ml-1" width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 0L0 5H10L5 0Z" fill=""></path>
                                                    </svg>
                                                ) : (
                                                    <svg className="fill-gray-500 inline-block mb-1 ml-1" width="10" height="5" viewBox="0 0 10 5" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M5 5L10 0L-4.37114e-07 8.74228e-07L5 5Z" fill=""></path>
                                                    </svg>
                                                )
                                            ) : ''}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </thead>
                <tbody  {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <VendaItem
                                key={row.id}
                                row={row}
                                onSelect={handleSelectVenda}
                                isSelected={selectedVendas.includes(row.original.id)}
                            />
                        );
                    })}
                </tbody>
            </table>

            <div className="flex items-center w-full mt-6 mb-4">
                <nav className="pagination isolate inline-flex space-x-1" aria-label="Pagination">
                    <button
                        onClick={() => previousPage()}
                        disabled={!canPreviousPage}
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Anterior</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"></path>
                        </svg>
                    </button>

                    {pageIndex !== 0 && (
                        <button
                            onClick={() => gotoPage(0)}
                            disabled={!canPreviousPage}
                            className={`relative inline-flex items-center justify-center font-medium rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canPreviousPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                        >
                            1
                        </button>
                    )}

                    {pageIndex > 1 && pageOptions.length > 4 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageOptions[pageIndex - 1] ? (
                        <button
                            onClick={() => gotoPage(pageIndex - 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex}
                        </button>
                    ) : null}


                    <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-white bg-secondary ring-1 ring-inset ring-secondary hover:bg-slate-700">
                        {pageIndex + 1}
                    </span>

                    {pageOptions[pageIndex + 1] && (
                        <button
                            onClick={() => gotoPage(pageIndex + 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageIndex + 2}
                        </button>
                    )}

                    {pageIndex < pageOptions.length - 3 && (
                        <span className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-gray-400 ring-1 ring-inset ring-gray-300">...</span>
                    )}

                    {pageIndex < pageOptions.length - 2 && (
                        <button
                            onClick={() => gotoPage(pageOptions.length - 1)}
                            className="relative inline-flex items-center justify-center font-medium rounded w-9 h-9 text-secondary ring-1 ring-inset ring-gray-300"
                        >
                            {pageOptions.length}
                        </button>
                    )}

                    <button
                        onClick={() => nextPage()}
                        disabled={!canNextPage}
                        className={`relative inline-flex items-center justify-center rounded w-9 h-9 ring-1 ring-inset ring-gray-300 ${canNextPage ? 'hover:text-white hover:bg-secondary hover:ring-secondary text-secondary' : 'text-slate-400'}`}
                    >
                        <span className="sr-only">Próximo</span>
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
                            <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"></path>
                        </svg>
                    </button>
                </nav>

                <div className="flex ml-auto pl-10 gap-3">
                    <div className="relative">
                        <button 
                            onClick={() => setShowExportDropdown(!showExportDropdown)}
                            className="bg-blue-500 text-white text-sm 2xl:text-base px-4 h-9 rounded transition-all hover:bg-blue-600"
                        >
                            <FontAwesomeIcon icon={faFileExport} className="mr-2" />
                            Exportar
                        </button>
                        
                        {showExportDropdown && (
                            <div className="absolute right-0 bottom-full mb-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                                <button
                                    onClick={() => handleExport('pendentes')}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-t-md"
                                >
                                    Exportar Pendentes
                                </button>
                                <button
                                    onClick={() => handleExport('aprovadas')}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                    Exportar Aprovadas
                                </button>
                                <button
                                    onClick={() => handleExport('reprovadas')}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-b-md"
                                >
                                    Exportar Reprovadas
                                </button>
                            </div>
                        )}
                    </div>

                    <button onClick={handleApproveSelected} className="bg-green-500 text-white text-sm 2xl:text-base px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedVendas.length <= 0 ? true : false}>
                        <FontAwesomeIcon icon={faCheck} className=" mr-2" />
                        Aprovar Selecionados
                    </button>

                    <button onClick={handleDisapproveSelected} className="bg-red-500 text-white text-sm 2xl:text-base px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedVendas.length <= 0 ? true : false}>
                        <FontAwesomeIcon icon={faClose} className=" mr-2" />
                        Reprovar Selecionados
                    </button>

                    <button onClick={handleDeleteSelected} className="bg-red-500 text-white text-sm 2xl:text-base px-4 h-9 rounded ml-auto transition-all enabled:hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed" disabled={selectedVendas.length <= 0 ? true : false}>
                        <FontAwesomeIcon icon={faTrash} className=" mr-2" />
                        Excluir Selecionados
                    </button>
                        
                </div>
            </div>

            {notification && notification.show && (
                <NotificationMessage
                    type={notification.type}
                    message={notification.message}
                    show={notification.show}
                    onClose={handleCloseNotification}
                />
            )}
        </>
    );
}

export default VendaList;